import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase.config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../contexts/AuthContext";
import { cities } from "../data/citiesData";
import {
  Home,
  MapPin,
  DollarSign,
  FileText,
  Bed,
  Bath,
  Ruler,
  ArrowLeft,
  Store,
  Map,
  Phone,
  PhoneCall,
  ZapIcon,
  Car,
  Shield,
  Wind,
  Flame,
  Droplets,
  Dumbbell,
  Waves,
  Zap,
  Building2,
  CpuIcon,
  School,
  Hospital,
  ShoppingBag,
  Landmark,
  Calendar,
  Receipt,
  Heart,
  Wifi,
  Coffee,
  Trees,
  Sun,
  Lock,
  Camera,
  Upload,
  X,
  ChevronRight,
  CheckCircle,
  Info,
  AlertCircle
} from "lucide-react";

const AddPropertyToAgent = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agent, setAgent] = useState(null);
  const [propertyImages, setPropertyImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [propertyCategory, setPropertyCategory] = useState("residential");

  // Pakistani Residential Property Types
  const residentialTypes = [
    { id: "lower-portion", name: "Lower Portion", urdu: "لوئر پورشن", icon: "🏘️" },
    { id: "upper-portion", name: "Upper Portion", urdu: "اپر پورشن", icon: "🏠" },
    { id: "full-house", name: "Full House", urdu: "مکمل مکان", icon: "🏡" },
    { id: "farmhouse", name: "Farmhouse", urdu: "فارم ہاؤس", icon: "🌾" },
    { id: "banglow", name: "Banglow", urdu: "بینگلو", icon: "🏛️" },
    { id: "apartment", name: "Apartment", urdu: "اپارٹمنٹ", icon: "🏢" },
  ];

  // Pakistani Plot Types
  const plotTypes = [
    { id: "residential-plot", name: "Residential Plot", urdu: "رہائشی پلاٹ", icon: "📐" },
    { id: "commercial-plot", name: "Commercial Plot", urdu: "کمرشل پلاٹ", icon: "🏪" },
    { id: "agricultural-land", name: "Agricultural Land", urdu: "زرعی زمین", icon: "🌽" },
    { id: "housing-scheme", name: "Housing Scheme Plot", urdu: "ہاؤسنگ سکیم پلاٹ", icon: "🏘️" },
  ];

  // Pakistani Commercial Types
  const commercialTypes = [
    { id: "shop", name: "Shop", urdu: "دکان", icon: "🛍️" },
    { id: "office", name: "Office", urdu: "آفس", icon: "💼" },
    { id: "godown", name: "Godown/Warehouse", urdu: "گودام", icon: "🏭" },
    { id: "showroom", name: "Showroom", urdu: "شو روم", icon: "✨" },
    { id: "restaurant", name: "Restaurant/Cafe", urdu: "ریسٹورنٹ", icon: "🍽️" },
    { id: "hotel", name: "Hotel/Guest House", urdu: "ہوٹل", icon: "🏨" },
    { id: "factory", name: "Factory/Industrial Unit", urdu: "فیکٹری", icon: "🏭" },
    { id: "plaza", name: "Commercial Plaza", urdu: "پلازہ", icon: "🏬" },
  ];

  // All property types grouped
  const propertyTypes = [
    { group: "🏠 Residential", types: residentialTypes },
    { group: "📐 Plots & Land", types: plotTypes },
    { group: "🏢 Commercial", types: commercialTypes },
  ];

  // Installment plan options
  const installmentPlans = [
    { id: "3-months", name: "3 Months", months: 3, urdu: "3 ماہ" },
    { id: "6-months", name: "6 Months", months: 6, urdu: "6 ماہ" },
    { id: "12-months", name: "12 Months", months: 12, urdu: "12 ماہ" },
    { id: "24-months", name: "24 Months", months: 24, urdu: "24 ماہ" },
    { id: "36-months", name: "36 Months", months: 36, urdu: "36 ماہ" },
    { id: "48-months", name: "48 Months", months: 48, urdu: "48 ماہ" },
    { id: "60-months", name: "60 Months (5 Years)", months: 60, urdu: "60 ماہ (5 سال)" },
    { id: "custom", name: "Custom Plan", months: 0, urdu: "اپنی مرضی" },
  ];

  const [propertyDetails, setPropertyDetails] = useState({
    type: "sale",
    propertyCategory: "residential",
    propertyType: "",
    title: "",
    description: "",
    location: "",
    society: "",
    city: "",
    area: "",
    bedrooms: 1,
    bathrooms: 1,
    marla: 0,
    kanal: 0,
    price: 0,
    discountedPrice: 0,
    offer: false,
    phoneNumber: "",
    whatsappNumber: "",
    features: {
      parking: false,
      garden: false,
      security: false,
      furnished: false,
      airConditioning: false,
      heating: false,
      pool: false,
      gym: false,
      laundry: false,
      backupElectricity: false,
      elevator: false,
      maintenanceStaff: false,
    },
    nearbyAmenities: {
      school: false,
      hospital: false,
      CpuIcon: false,
      market: false,
      park: false,
      bank: false,
    },
    agentId: agentId,
    agentName: "",
    agentCompany: "",
    agentPhone: "",
    userRef: agentId,
    pricePerMarla: 0,
    installmentAvailable: false,
    installmentPlan: "12-months",
    customInstallmentMonths: 12,
    downPayment: 0,
    monthlyInstallment: 0,
  });

  // Fetch agent details
  useEffect(() => {
    if (!agentId) {
      toast.error("Invalid agent ID");
      navigate("/featured-agents");
      return;
    }
    
    const fetchAgent = async () => {
      try {
        const agentDoc = await getDoc(doc(db, "Featured-Agents", agentId));
        if (agentDoc.exists()) {
          const agentData = agentDoc.data();
          setAgent(agentData);
          
          setPropertyDetails((prev) => ({
            ...prev,
            agentName: agentData?.sellerName || "",
            agentCompany: agentData?.company || "",
            agentPhone: agentData?.phoneNumber || "",
            phoneNumber: agentData?.phoneNumber || "",
            whatsappNumber: agentData?.whatsappNumber || agentData?.phoneNumber || "",
          }));
        } else {
          toast.error("Agent not found");
          navigate("/featured-agents");
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching agent:", error);
        toast.error("Failed to load agent details");
        setLoading(false);
      }
    };

    fetchAgent();
  }, [agentId, navigate]);

  // Calculate price per marla
  const calculatePricePerMarla = () => {
    if (propertyDetails.marla > 0 && propertyDetails.price > 0) {
      const pricePerMarla = propertyDetails.price / propertyDetails.marla;
      setPropertyDetails(prev => ({ ...prev, pricePerMarla }));
    }
  };

  // Calculate monthly installment
  const calculateMonthlyInstallment = () => {
    if (!propertyDetails.installmentAvailable) return 0;
    
    const price = propertyDetails.offer && propertyDetails.discountedPrice > 0 
      ? propertyDetails.discountedPrice 
      : propertyDetails.price;
    
    const downPaymentAmount = propertyDetails.downPayment || 0;
    const remainingAmount = price - downPaymentAmount;
    
    let months = propertyDetails.installmentMonths;
    if (propertyDetails.installmentPlan === "custom") {
      months = propertyDetails.customInstallmentMonths;
    } else {
      const selectedPlan = installmentPlans.find(plan => plan.id === propertyDetails.installmentPlan);
      months = selectedPlan ? selectedPlan.months : 12;
    }
    
    if (months > 0 && remainingAmount > 0) {
      const monthlyAmount = remainingAmount / months;
      setPropertyDetails(prev => ({ ...prev, monthlyInstallment: Math.round(monthlyAmount) }));
      return Math.round(monthlyAmount);
    }
    return 0;
  };

  // Get installment months
  const getInstallmentMonths = () => {
    if (propertyDetails.installmentPlan === "custom") {
      return propertyDetails.customInstallmentMonths;
    }
    const selectedPlan = installmentPlans.find(plan => plan.id === propertyDetails.installmentPlan);
    return selectedPlan ? selectedPlan.months : 12;
  };

  // Handle form changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      if (name in propertyDetails.features) {
        setPropertyDetails((prev) => ({
          ...prev,
          features: {
            ...prev.features,
            [name]: checked,
          },
        }));
      } else if (name in propertyDetails.nearbyAmenities) {
        setPropertyDetails((prev) => ({
          ...prev,
          nearbyAmenities: {
            ...prev.nearbyAmenities,
            [name]: checked,
          },
        }));
      } else {
        setPropertyDetails((prev) => ({
          ...prev,
          [name]: checked,
        }));
        
        if (name === "installmentAvailable") {
          setTimeout(() => calculateMonthlyInstallment(), 100);
        }
      }
    } else if (type === "number") {
      const numValue = Number(value);
      setPropertyDetails((prev) => ({
        ...prev,
        [name]: numValue,
      }));
      if (name === "marla" || name === "price") {
        setTimeout(calculatePricePerMarla, 100);
      }
      if (name === "downPayment" || name === "price" || name === "discountedPrice" || 
          name === "customInstallmentMonths" || name === "installmentPlan") {
        setTimeout(calculateMonthlyInstallment, 100);
      }
    } else {
      setPropertyDetails((prev) => ({
        ...prev,
        [name]: value,
      }));
      
      if (name === "installmentPlan") {
        setTimeout(calculateMonthlyInstallment, 100);
      }
    }
  };

  const handleCategoryChange = (category) => {
    setPropertyCategory(category);
    setPropertyDetails(prev => ({
      ...prev,
      propertyCategory: category,
      propertyType: "",
    }));
  };

  // Handle image upload
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + propertyImages.length > 10) {
      toast.error("Maximum 10 images allowed");
      return;
    }

    setPropertyImages((prev) => [...prev, ...files]);

    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setImagePreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeImage = (index) => {
    setPropertyImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Get available areas based on selected city
  const getAreasForCity = () => {
    const selectedCity = cities.find(c => c.name === propertyDetails.city);
    return selectedCity ? selectedCity.areas : [];
  };

  // Submit property
  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredFields = ['propertyType', 'title', 'location', 'city', 'price'];
    
    for (const field of requiredFields) {
      if (!propertyDetails[field] && propertyDetails[field] !== 0) {
        toast.error(`Please fill in the ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} field`);
        return;
      }
    }

    if (propertyImages.length === 0) {
      toast.error("Please upload at least one property image");
      return;
    }

    if (propertyDetails.offer && propertyDetails.discountedPrice >= propertyDetails.price) {
      toast.error("Discounted price must be less than original price");
      return;
    }

    if (propertyDetails.installmentAvailable) {
      if (propertyDetails.downPayment >= propertyDetails.price) {
        toast.error("Down payment must be less than total price");
        return;
      }
      
      const months = getInstallmentMonths();
      if (months <= 0) {
        toast.error("Please select valid installment duration");
        return;
      }
    }

    setSubmitting(true);

    try {
      const imageUrls = [];
      for (const image of propertyImages) {
        const storageRef = ref(
          storage,
          `property-images/${agentId}/${Date.now()}_${image.name}`
        );
        await uploadBytes(storageRef, image);
        const downloadURL = await getDownloadURL(storageRef);
        imageUrls.push(downloadURL);
      }

      const finalPrice = propertyDetails.offer && propertyDetails.discountedPrice > 0 
        ? propertyDetails.discountedPrice 
        : propertyDetails.price;
      
      const installmentMonths = propertyDetails.installmentAvailable ? getInstallmentMonths() : 0;
      const finalMonthlyInstallment = propertyDetails.installmentAvailable 
        ? (finalPrice - (propertyDetails.downPayment || 0)) / installmentMonths
        : 0;

      const propertyData = {
        ...propertyDetails,
        price: Number(propertyDetails.price),
        discountedPrice: Number(propertyDetails.discountedPrice) || 0,
        marla: Number(propertyDetails.marla) || 0,
        kanal: Number(propertyDetails.kanal) || 0,
        bedrooms: Number(propertyDetails.bedrooms) || 1,
        bathrooms: Number(propertyDetails.bathrooms) || 1,
        area: Number(propertyDetails.area) || 0,
        pricePerMarla: Number(propertyDetails.pricePerMarla) || 0,
        propertyImages: imageUrls,
        thumbnail: imageUrls[0],
        timestamp: serverTimestamp(),
        createdAt: new Date(),
        updatedAt: new Date(),
        finalPrice: finalPrice,
        installmentAvailable: propertyDetails.installmentAvailable,
        installmentMonths: installmentMonths,
        installmentPlanName: propertyDetails.installmentPlan === "custom" 
          ? `${propertyDetails.customInstallmentMonths} Months (Custom)`
          : installmentPlans.find(p => p.id === propertyDetails.installmentPlan)?.name || "",
        downPayment: Number(propertyDetails.downPayment) || 0,
        monthlyInstallment: Math.round(finalMonthlyInstallment),
        totalInstallmentAmount: finalPrice - (propertyDetails.downPayment || 0),
      };

      Object.keys(propertyData).forEach(key => {
        if (propertyData[key] === undefined || propertyData[key] === null) {
          delete propertyData[key];
        }
      });

      await addDoc(collection(db, "properties"), propertyData);

      toast.success("Property added successfully! ملکیت کامیابی سے شامل کر دی گئی");
      setTimeout(() => {
        navigate(`/agent-profile/${agentId}`);
      }, 2000);
    } catch (error) {
      console.error("Error adding property:", error);
      toast.error("Failed to add property");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  const getCurrentPropertyTypes = () => {
    const group = propertyTypes.find(g => 
      g.group.toLowerCase().includes(propertyCategory)
    );
    return group ? group.types : residentialTypes;
  };

  const effectivePrice = propertyDetails.offer && propertyDetails.discountedPrice > 0 
    ? propertyDetails.discountedPrice 
    : propertyDetails.price;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pb-12">
      <ToastContainer position="top-right" autoClose={5000} />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => navigate(`/agent-profile/${agentId}`)}
                className="flex items-center text-emerald-600 hover:text-emerald-700 mb-4 transition-colors"
              >
                <ArrowLeft className="mr-2 h-5 w-5" />
                Back to Agency Profile
              </button>
              <h1 className="text-3xl font-bold text-gray-900">
                Add New Property | نئی پراپرٹی شامل کریں
              </h1>
              <p className="text-gray-600 mt-2">
                Adding property for {agent?.sellerName}
              </p>
            </div>
            {agent?.thumbnail && (
              <img
                src={agent.thumbnail}
                alt={agent.sellerName}
                className="h-16 w-16 rounded-full object-cover border-2 border-emerald-500"
              />
            )}
          </div>
        </div>

        {/* Property Form */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <form onSubmit={handleSubmit} className="space-y-8 p-6">
            
            {/* Listing Type */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Listing Type | لسٹنگ کی قسم
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div 
                  className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-300 ${
                    propertyDetails.type === 'sale' 
                      ? 'border-emerald-500 bg-emerald-50 shadow-md' 
                      : 'border-gray-200 hover:border-emerald-300 hover:shadow-sm'
                  }`}
                  onClick={() => setPropertyDetails(prev => ({ ...prev, type: 'sale' }))}
                >
                  <div className="text-center">
                    <DollarSign className="h-8 w-8 mx-auto mb-2 text-emerald-600" />
                    <h3 className="font-semibold text-lg">For Sale | فروخت کے لیے</h3>
                    <p className="text-sm text-gray-600">List for one-time sale</p>
                  </div>
                </div>

                <div 
                  className={`border-2 rounded-xl p-4 cursor-pointer transition-all duration-300 ${
                    propertyDetails.type === 'rent' 
                      ? 'border-emerald-500 bg-emerald-50 shadow-md' 
                      : 'border-gray-200 hover:border-emerald-300 hover:shadow-sm'
                  }`}
                  onClick={() => setPropertyDetails(prev => ({ ...prev, type: 'rent' }))}
                >
                  <div className="text-center">
                    <Home className="h-8 w-8 mx-auto mb-2 text-emerald-600" />
                    <h3 className="font-semibold text-lg">For Rent | کرائے پر</h3>
                    <p className="text-sm text-gray-600">List for monthly rental</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Property Category Tabs */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Property Category | پراپرٹی کی قسم
              </h3>
              <div className="flex gap-4 mb-6">
                {[
                  { id: "residential", label: "Residential", icon: <Home className="h-5 w-5" />, urdu: "رہائشی" },
                  { id: "plots", label: "Plots & Land", icon: <Map className="h-5 w-5" />, urdu: "پلاٹ اور زمین" },
                  { id: "commercial", label: "Commercial", icon: <Store className="h-5 w-5" />, urdu: "کمرشل" },
                ].map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => handleCategoryChange(category.id)}
                    className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                      propertyCategory === category.id
                        ? "bg-gradient-to-r from-emerald-600 to-green-600 text-white shadow-lg transform scale-105"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {category.icon}
                    <span>{category.label}</span>
                    <span className="text-xs opacity-80">({category.urdu})</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Property Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Property Type | پراپرٹی کی قسم <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {getCurrentPropertyTypes().map(type => (
                  <div
                    key={type.id}
                    onClick={() => setPropertyDetails(prev => ({ ...prev, propertyType: type.id }))}
                    className={`border-2 rounded-lg p-3 cursor-pointer transition-all duration-300 ${
                      propertyDetails.propertyType === type.id
                        ? 'border-emerald-500 bg-emerald-50 shadow-md'
                        : 'border-gray-200 hover:border-emerald-300'
                    }`}
                  >
                    <div className="text-center">
                      <span className="text-2xl block mb-1">{type.icon}</span>
                      <p className="font-medium text-sm">{type.name}</p>
                      <p className="text-xs text-gray-500">{type.urdu}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Property Title | پراپرٹی کا عنوان <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={propertyDetails.title}
                  onChange={handleChange}
                  placeholder="e.g., 5 Marla House in DHA Phase 3"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Price | قیمت <span className="text-red-500">*</span> (PKR)
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="number"
                    name="price"
                    value={propertyDetails.price}
                    onChange={handleChange}
                    placeholder={propertyDetails.type === 'sale' ? "50,00,000" : "50,000"}
                    className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {propertyDetails.type === 'sale' ? 'Total property price in Rupees' : 'Monthly rent amount in Rupees'}
                </p>
              </div>
            </div>

            {/* Offer Section */}
            {propertyDetails.type === 'sale' && (
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-5 rounded-xl border border-yellow-200">
                <div className="flex items-center space-x-4 mb-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="offer"
                      checked={propertyDetails.offer}
                      onChange={handleChange}
                      className="rounded text-emerald-600 focus:ring-emerald-500 w-5 h-5"
                    />
                    <span className="font-medium text-gray-700">Offer Discount? | تخفیف دیں؟</span>
                  </label>

                  {propertyDetails.offer && (
                    <div className="flex-1 max-w-xs">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Discounted Price | تخفیف شدہ قیمت
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        <input
                          type="number"
                          name="discountedPrice"
                          value={propertyDetails.discountedPrice}
                          onChange={handleChange}
                          min="0"
                          max={propertyDetails.price}
                          className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  )}
                </div>
                {propertyDetails.offer && propertyDetails.discountedPrice > 0 && (
                  <p className="text-sm text-green-600 font-semibold">
                    💰 Discount Amount: PKR {(propertyDetails.price - propertyDetails.discountedPrice).toLocaleString()}
                  </p>
                )}
              </div>
            )}

            {/* Installment Section */}
            {propertyDetails.type === 'sale' && effectivePrice > 0 && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-5 rounded-xl border border-green-200">
                <div className="flex items-center space-x-4 mb-6">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="installmentAvailable"
                      checked={propertyDetails.installmentAvailable}
                      onChange={handleChange}
                      className="rounded text-green-600 focus:ring-green-500 w-5 h-5"
                    />
                    <span className="font-medium text-gray-700">
                      <Receipt className="inline h-5 w-5 mr-2 text-green-600" />
                      Offer Easy Installments? | قسطوں کی سہولت دیں؟ (No Interest | بغیر سود)
                    </span>
                  </label>
                </div>

                {propertyDetails.installmentAvailable && (
                  <div className="space-y-4 ml-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Calendar className="inline h-4 w-4 mr-2" />
                        Installment Duration | قسطوں کی مدت <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {installmentPlans.map((plan) => (
                          <label key={plan.id} className="flex items-center space-x-2 p-2 rounded-lg hover:bg-green-50 cursor-pointer border border-gray-200">
                            <input
                              type="radio"
                              name="installmentPlan"
                              value={plan.id}
                              checked={propertyDetails.installmentPlan === plan.id}
                              onChange={handleChange}
                              className="text-green-600 focus:ring-green-500"
                            />
                            <span className="text-sm text-gray-700">
                              {plan.name}
                              <span className="text-xs text-gray-400 block">({plan.urdu})</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {propertyDetails.installmentPlan === "custom" && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Number of Months | ماہ کی تعداد <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="customInstallmentMonths"
                          value={propertyDetails.customInstallmentMonths}
                          onChange={handleChange}
                          min="1"
                          max="120"
                          className="w-full md:w-64 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Down Payment | پیشگی ادائیگی (PKR)
                      </label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                        <input
                          type="number"
                          name="downPayment"
                          value={propertyDetails.downPayment}
                          onChange={handleChange}
                          min="0"
                          max={effectivePrice}
                          placeholder="0"
                          className="w-full md:w-96 pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Minimum down payment is 0 PKR (No interest, just equal installments)
                      </p>
                    </div>

                    {(() => {
                      const months = getInstallmentMonths();
                      const remainingAmount = effectivePrice - (propertyDetails.downPayment || 0);
                      const monthly = months > 0 ? remainingAmount / months : 0;
                      return (
                        <div className="bg-white rounded-lg p-4 border border-green-200 mt-4">
                          <h4 className="font-semibold text-gray-800 mb-3">Installment Summary | قسطوں کی تفصیل</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Total Price:</span>
                              <span className="font-medium">PKR {effectivePrice.toLocaleString()}</span>
                            </div>
                            {propertyDetails.downPayment > 0 && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Down Payment:</span>
                                <span className="font-medium text-red-600">- PKR {propertyDetails.downPayment.toLocaleString()}</span>
                              </div>
                            )}
                            <div className="flex justify-between border-t pt-2">
                              <span className="text-gray-600">Remaining Amount:</span>
                              <span className="font-semibold">PKR {Math.round(remainingAmount).toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Duration:</span>
                              <span className="font-medium">{months} Months</span>
                            </div>
                            <div className="flex justify-between bg-green-50 p-2 rounded-lg mt-2">
                              <span className="font-semibold text-green-700">Monthly Installment:</span>
                              <span className="font-bold text-green-700 text-lg">
                                PKR {Math.round(monthly).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-xs text-green-600 text-center mt-2">
                              ✓ No Interest / Markup (بغیر سود کے)
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Property Size */}
            {(propertyCategory === "residential" || propertyCategory === "plots") && (
              <div className="bg-gray-50 p-5 rounded-xl">
                <h3 className="text-md font-semibold text-gray-900 mb-4">
                  Property Size (Pakistani Units) | پراپرٹی کا سائز
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Marla | مرلہ
                    </label>
                    <input
                      type="number"
                      name="marla"
                      value={propertyDetails.marla}
                      onChange={handleChange}
                      min="0"
                      step="0.5"
                      placeholder="e.g., 5, 10, 20"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kanal | کنال
                    </label>
                    <input
                      type="number"
                      name="kanal"
                      value={propertyDetails.kanal}
                      onChange={handleChange}
                      min="0"
                      step="0.5"
                      placeholder="e.g., 1, 2, 5"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                </div>
                {propertyDetails.marla > 0 && propertyDetails.price > 0 && (
                  <p className="mt-3 text-sm text-green-600 font-semibold">
                    📊 Price per Marla: PKR {Math.round(propertyDetails.pricePerMarla).toLocaleString()}
                  </p>
                )}
              </div>
            )}

            {/* Property Specifications */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Property Specifications | پراپرٹی کی تفصیلات
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {propertyCategory === "residential" && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Bed className="inline h-4 w-4 mr-2" />
                        Bedrooms | بیڈروم
                      </label>
                      <input
                        type="number"
                        name="bedrooms"
                        value={propertyDetails.bedrooms}
                        onChange={handleChange}
                        min="0"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <Bath className="inline h-4 w-4 mr-2" />
                        Bathrooms | باتھ روم
                      </label>
                      <input
                        type="number"
                        name="bathrooms"
                        value={propertyDetails.bathrooms}
                        onChange={handleChange}
                        min="0"
                        step="0.5"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Ruler className="inline h-4 w-4 mr-2" />
                    Area | رقبہ (sq ft)
                  </label>
                  <input
                    type="number"
                    name="area"
                    value={propertyDetails.area}
                    onChange={handleChange}
                    min="0"
                    placeholder="2000"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Location | مقام
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City | شہر <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="city"
                    value={propertyDetails.city}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select City</option>
                    {cities.map(city => (
                      <option key={city.id} value={city.name}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Society/Area | سوسائٹی/علاقہ
                  </label>
                  <select
                    name="society"
                    value={propertyDetails.society}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    disabled={!propertyDetails.city}
                  >
                    <option value="">Select Area/Society</option>
                    {getAreasForCity().map(area => (
                      <option key={area} value={area}>
                        {area}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Address | پورا پتہ <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                    <input
                      type="text"
                      name="location"
                      value={propertyDetails.location}
                      onChange={handleChange}
                      placeholder="Street, Block, House Number"
                      className="w-full pl-10 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Contact Information | رابطے کی معلومات
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="inline h-4 w-4 mr-2" />
                    Phone Number | فون نمبر <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={propertyDetails.phoneNumber}
                    onChange={handleChange}
                    placeholder="03XX XXXXXXX"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <PhoneCall className="inline h-4 w-4 mr-2" />
                    WhatsApp Number (Optional)
                  </label>
                  <input
                    type="tel"
                    name="whatsappNumber"
                    value={propertyDetails.whatsappNumber}
                    onChange={handleChange}
                    placeholder="03XX XXXXXXX"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agent Name | ایجنٹ کا نام
                  </label>
                  <input
                    type="text"
                    value={propertyDetails.agentName}
                    disabled
                    className="w-full p-3 bg-gray-100 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            </div>

            {/* Features & Amenities */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Features & Amenities | خصوصیات اور سہولیات
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[
                  { key: "parking", label: "Parking", icon: <Car className="h-5 w-5" />, urdu: "پارکنگ" },
                  { key: "garden", label: "Garden", icon: <Trees className="h-5 w-5" />, urdu: "باغیچہ" },
                  { key: "security", label: "24/7 Security", icon: <Shield className="h-5 w-5" />, urdu: "سیکیورٹی" },
                  { key: "furnished", label: "Furnished", icon: <Home className="h-5 w-5" />, urdu: "فرنشڈ" },
                  { key: "airConditioning", label: "Air Conditioning", icon: <Wind className="h-5 w-5" />, urdu: "اے سی" },
                  { key: "heating", label: "Heating", icon: <Flame className="h-5 w-5" />, urdu: "ہیٹنگ" },
                  { key: "pool", label: "Swimming Pool", icon: <Droplets className="h-5 w-5" />, urdu: "سوئمنگ پول" },
                  { key: "gym", label: "Gym", icon: <Dumbbell className="h-5 w-5" />, urdu: "جم" },
                  { key: "laundry", label: "Laundry", icon: <Waves className="h-5 w-5" />, urdu: "لانڈری" },
                  { key: "backupElectricity", label: "Backup Electricity", icon: <Zap className="h-5 w-5" />, urdu: "بیک اپ بجلی" },
                  { key: "elevator", label: "Elevator", icon: <Building2 className="h-5 w-5" />, urdu: "لفٹ" },
                  { key: "maintenanceStaff", label: "Maintenance Staff", icon: <Shield className="h-5 w-5" />, urdu: "مینٹیننس" },
                ].map((feature) => (
                  <label key={feature.key} className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      name={feature.key}
                      checked={propertyDetails.features[feature.key]}
                      onChange={handleChange}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    {feature.icon}
                    <span className="text-sm text-gray-700">
                      {feature.label} <span className="text-xs text-gray-400">({feature.urdu})</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Nearby Amenities */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Nearby Amenities | قریبی سہولیات
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {[
                  { key: "school", label: "School", icon: <School className="h-5 w-5" />, urdu: "اسکول" },
                  { key: "hospital", label: "Hospital", icon: <Hospital className="h-5 w-5" />, urdu: "ہسپتال" },
                  { key: "CpuIcon", label: "CpuIcon", icon: <CpuIcon className="h-5 w-5" />, urdu: "مسجد" },
                  { key: "market", label: "Market", icon: <ShoppingBag className="h-5 w-5" />, urdu: "مارکیٹ" },
                  { key: "park", label: "Park", icon: <ZapIcon className="h-5 w-5" />, urdu: "پارک" },
                  { key: "bank", label: "Bank", icon: <Landmark className="h-5 w-5" />, urdu: "بینک" },
                ].map((amenity) => (
                  <label key={amenity.key} className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      name={amenity.key}
                      checked={propertyDetails.nearbyAmenities[amenity.key]}
                      onChange={handleChange}
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    {amenity.icon}
                    <span className="text-sm text-gray-700">
                      {amenity.label} <span className="text-xs text-gray-400">({amenity.urdu})</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="inline h-4 w-4 mr-2" />
                Description | تفصیل
              </label>
              <textarea
                name="description"
                value={propertyDetails.description}
                onChange={handleChange}
                placeholder="Describe the property features, neighborhood, nearby amenities, and unique selling points..."
                rows="4"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>

            {/* Image Upload */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Property Images | پراپرٹی کی تصاویر <span className="text-red-500">*</span>
              </h3>
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center transition-all hover:border-emerald-400">
                <input
                  type="file"
                  id="property-images"
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <label htmlFor="property-images" className="cursor-pointer block">
                  <Camera className="mx-auto h-16 w-16 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-600">
                    Click to upload property images
                  </p>
                  <p className="text-xs text-gray-500">
                    JPG, PNG, WebP (Max 10 images, first image will be cover)
                  </p>
                  <button
                    type="button"
                    className="mt-4 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all duration-300 transform hover:scale-105"
                    onClick={() => document.getElementById('property-images').click()}
                  >
                    Browse Files
                  </button>
                </label>
              </div>

              {imagePreviews.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Selected Images ({imagePreviews.length}/10)
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {imagePreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="h-28 w-full object-cover rounded-lg shadow-md"
                        />
                        {index === 0 && (
                          <span className="absolute top-2 left-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs px-2 py-1 rounded-md shadow">
                            Cover 📸
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    💡 Tip: The first image will be displayed as the main thumbnail
                  </p>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate(`/agent-profile/${agentId}`)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel | منسوخ کریں
              </button>
              <button
                type="submit"
                disabled={submitting}
                className={`px-8 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-lg hover:from-emerald-700 hover:to-green-700 transition-all duration-300 transform hover:scale-105 ${
                  submitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {submitting ? "Adding Property..." : "Add Property | پراپرٹی شامل کریں"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddPropertyToAgent;